import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { worldFaiths, universalThemes } from '@/data/frameworks'
import { cn } from '@/lib/utils'

export default function Step5() {
  const [selectedFaith, setSelectedFaith] = useState<string | null>(null)
  const [selectedTheme, setSelectedTheme] = useState<string | null>(null)

  const activeFaith = selectedFaith
    ? worldFaiths.find(f => f.id === selectedFaith)
    : null

  const activeTheme = selectedTheme
    ? universalThemes.find(t => t.id === selectedTheme)
    : null

  return (
    <div className="space-y-8">
      {/* Introduction */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center max-w-3xl mx-auto"
      >
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-gold-400 to-gold-600 mb-6 shadow-divine">
          <span className="text-4xl">🔍</span>
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold text-gold-gradient mb-6">
          Interactive Wisdom Map
        </h1>
        <p className="text-lg text-gold-700/80 dark:text-gold-300/80 leading-relaxed">
          Explore the connections between world faiths and universal themes.
          Select a faith tradition or theme to discover how they interrelate.
        </p>
      </motion.div>

      {/* World Faiths Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="space-y-6"
      >
        <div className="text-center">
          <h2 className="font-display text-2xl font-semibold text-gold-800 dark:text-gold-200 mb-2">
            World Faith Traditions
          </h2>
          <p className="text-gold-600/70 dark:text-gold-400/70">
            Select a tradition to explore its teachings and connections
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {worldFaiths.map((faith, index) => (
            <motion.button
              key={faith.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
              onClick={() => {
                setSelectedFaith(selectedFaith === faith.id ? null : faith.id)
                setSelectedTheme(null)
              }}
              className={cn(
                'p-4 rounded-xl text-center transition-all duration-300',
                selectedFaith === faith.id
                  ? 'bg-gold-500 text-white shadow-divine scale-105'
                  : 'sacred-card hover:shadow-sacred-lg hover:scale-105'
              )}
            >
              <div
                className={cn(
                  'w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center text-3xl transition-all duration-300',
                  selectedFaith === faith.id
                    ? 'bg-white/20'
                    : 'bg-gold-100 dark:bg-gold-900/30'
                )}
                style={{
                  borderColor: selectedFaith === faith.id ? 'white' : faith.color,
                  borderWidth: '3px'
                }}
              >
                {faith.symbol}
              </div>
              <h3
                className={cn(
                  'font-display font-semibold text-sm transition-all duration-300',
                  selectedFaith === faith.id
                    ? 'text-white'
                    : 'text-gold-800 dark:text-gold-200'
                )}
              >
                {faith.name}
              </h3>
            </motion.button>
          ))}
        </div>
      </motion.section>

      {/* Selected Faith Details */}
      <AnimatePresence mode="wait">
        {activeFaith && (
          <motion.div
            key={activeFaith.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="sacred-card p-8"
          >
            <div className="flex items-start gap-6 mb-6">
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center text-4xl flex-shrink-0"
                style={{
                  backgroundColor: `${activeFaith.color}20`,
                  borderColor: activeFaith.color,
                  borderWidth: '3px'
                }}
              >
                {activeFaith.symbol}
              </div>
              <div>
                <h3 className="font-display text-2xl font-bold text-gold-800 dark:text-gold-200 mb-2">
                  {activeFaith.name}
                </h3>
                <p className="text-gold-700/80 dark:text-gold-300/80">
                  {activeFaith.description}
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-display font-semibold text-gold-700 dark:text-gold-300 mb-3">
                  Core Beliefs
                </h4>
                <ul className="space-y-2">
                  {activeFaith.coreBeliefs.map((belief) => (
                    <li
                      key={belief}
                      className="flex items-start gap-2 text-sm text-gold-600/80 dark:text-gold-400/80"
                    >
                      <span className="text-gold-500 mt-0.5">•</span>
                      {belief}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-display font-semibold text-gold-700 dark:text-gold-300 mb-3">
                  Sacred Texts
                </h4>
                <div className="flex flex-wrap gap-2">
                  {activeFaith.sacredTexts.map((text) => (
                    <span
                      key={text}
                      className="px-3 py-1 rounded-full text-sm bg-gold-100 dark:bg-gold-900/30 text-gold-700 dark:text-gold-300"
                    >
                      {text}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <button
              onClick={() => setSelectedFaith(null)}
              className="mt-6 text-sm text-gold-500 hover:text-gold-600 transition-all duration-300"
            >
              Close details
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Universal Themes Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="space-y-6"
      >
        <div className="text-center">
          <h2 className="font-display text-2xl font-semibold text-gold-800 dark:text-gold-200 mb-2">
            Universal Spiritual Themes
          </h2>
          <p className="text-gold-600/70 dark:text-gold-400/70">
            Themes that transcend individual traditions and connect all seekers
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {universalThemes.map((theme, index) => (
            <motion.button
              key={theme.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 + index * 0.05 }}
              onClick={() => {
                setSelectedTheme(selectedTheme === theme.id ? null : theme.id)
                setSelectedFaith(null)
              }}
              className={cn(
                'p-6 rounded-xl text-left transition-all duration-300',
                selectedTheme === theme.id
                  ? 'bg-gold-500 text-white shadow-divine'
                  : 'sacred-card hover:shadow-sacred-lg'
              )}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">{theme.icon}</span>
                <h3
                  className={cn(
                    'font-display font-semibold transition-all duration-300',
                    selectedTheme === theme.id
                      ? 'text-white'
                      : 'text-gold-800 dark:text-gold-200'
                  )}
                >
                  {theme.theme}
                </h3>
              </div>
              <p
                className={cn(
                  'text-sm transition-all duration-300',
                  selectedTheme === theme.id
                    ? 'text-white/80'
                    : 'text-gold-600/70 dark:text-gold-400/70'
                )}
              >
                {theme.description}
              </p>
            </motion.button>
          ))}
        </div>
      </motion.section>

      {/* Selected Theme Details */}
      <AnimatePresence mode="wait">
        {activeTheme && (
          <motion.div
            key={activeTheme.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="sacred-card p-8"
          >
            <div className="flex items-center gap-4 mb-6">
              <span className="text-4xl">{activeTheme.icon}</span>
              <div>
                <h3 className="font-display text-2xl font-bold text-gold-800 dark:text-gold-200">
                  {activeTheme.theme}
                </h3>
                <p className="text-gold-600/70 dark:text-gold-400/70">
                  {activeTheme.description}
                </p>
              </div>
            </div>

            <div>
              <h4 className="font-display font-semibold text-gold-700 dark:text-gold-300 mb-4">
                How Different Traditions Express This Theme
              </h4>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {activeTheme.traditions.map((trad, index) => (
                  <motion.div
                    key={trad.faith}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="p-4 rounded-lg bg-gold-50 dark:bg-gold-900/20 transition-all duration-300 hover:bg-gold-100 dark:hover:bg-gold-900/30"
                  >
                    <h5 className="font-semibold text-gold-800 dark:text-gold-200 mb-1">
                      {trad.faith}
                    </h5>
                    <p className="text-sm text-gold-600/80 dark:text-gold-400/80">
                      {trad.teaching}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>

            <button
              onClick={() => setSelectedTheme(null)}
              className="mt-6 text-sm text-gold-500 hover:text-gold-600 transition-all duration-300"
            >
              Close details
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Legend */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="flex flex-wrap justify-center gap-8 pt-4"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-purple-200 border-2 border-purple-500" />
          <span className="text-sm text-gold-600 dark:text-gold-400">World Faiths</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-r from-gold-200 to-gold-400 border-2 border-gold-500" />
          <span className="text-sm text-gold-600 dark:text-gold-400">Universal Themes</span>
        </div>
      </motion.div>

      {/* Instructions */}
      <div className="text-center text-sm text-gold-600/60 dark:text-gold-400/60">
        <p>Select faith traditions or themes above to explore their teachings and connections.</p>
      </div>
    </div>
  )
}
